<template>
	<div class="relative flex-grow">
		<EquipmentSharedDeviceToolTestPanel
			:variant-groups="variantGroups"
			@set-variant="setVariant"
			@reset="resetVariant"
		/>

		<EquipmentSharedDeviceToolCard
			ref="statusCardRef"
			:progress="progress"
			:is-loading="isChecking"
			:is-device-supported="isDeviceSupported"
			:has-serial-number="!!serialNumber"
			:requires-serial-number="requiresSerialNumber"
			:status-text="testStatus"
			:show-result="!!(showResult && !!statusResult)"
			:error-keys="{
				notSupported: 'device-tools.status.not-supported',
				missingSerial: 'device-tools.status.missing-serial',
				failed: 'device-tools.status.failed'
			}"
			i18n-prefix="device-tools.status"
			:cooldown-seconds="cooldownSeconds"
			:status-label="statusLabel"
			:device-info="statusResult ? { type: statusResult.deviceType, brand: statusResult.deviceBrand } : null"
			:severity="severity"
			:button-config="{
				labelKey: 'device-tools.status.button-label',
				icon: SignalIcon,
				hideWhenUnsupported: true
			}"
			show-unsupported-message
			unsupported-message-key="device-tools.status.unsupported"
			@button-click="handleCheck"
		>
		<!-- Title -->
		<template #title>
			{{ t('device-tools.status.title') }}
		</template>

		<!-- Hint: Use parent default with custom hint-text sub-slot -->
		<template #hint-text>
			{{ t(showResult ? 'device-tools.status.hint-again' : 'device-tools.status.hint') }}
		</template>

		<!-- Result: Only override default content when we have actual result with content -->
		<template v-if="showResult && statusResult && statusDescription" #result-line1>
			{{ statusDescription }}
		</template>

		<template v-if="showResult && statusResult && statusMessage" #result-line2>
			{{ statusMessage }}
		</template>

		<!-- Additional Details (Technical Information) -->
		<template #additional-details>
			<EquipmentSharedTechnicalDetailsPanel
				v-if="showResult && statusResult?.fullDetails"
				:details="statusResult.fullDetails"
			/>
		</template>
	</EquipmentSharedDeviceToolCard>
</div>
</template>

<script setup lang="ts">
import { useDeviceTools, useDeviceToolState } from '@/composables/equipment'
import { DEVICE_STATUS_MOCKS, getDeviceStatusVariantGroups } from '@/composables/equipment/useDeviceToolTesting'
import type { DeviceStatusResponse } from '@/types/api-client/device-tools'
import { isUispOffline, isUispOnline } from '@/utils/equipment/deviceHealth'
import { NMS_TYPES } from '@/utils/equipment/nmsDetection'
import { getStatusDescription, getStatusLabel, getStatusMessage } from '@/utils/equipment/statusHelpers'
import { SignalIcon } from '@heroicons/vue/24/outline'
import { useI18n } from 'vue-i18n'

const { t } = useI18n()

const props = defineProps({
	macAddress: {
		type: String,
		required: true
	},
	serialNumber: {
		type: [String, null] as PropType<string | null>,
		default: null
	},
	deviceNms: {
		type: String,
		default: ''
	},
	config: {
		type: Object as PropType<{ apiDomain: 'internal' | 'customer' }>,
		required: true,
		default: () => ({ apiDomain: 'customer' })
	}
})

const { macAddress, serialNumber, deviceNms, config } = toRefs(props)

// Use centralized testing configuration
const variantGroups = getDeviceStatusVariantGroups()

const { checkDeviceStatus, deviceStatusTests, getDeviceKey, resetDeviceStatus, injectMockDeviceStatus } = useDeviceTools(config.value)

const setVariant = (mockId: string) => {
	const mock = DEVICE_STATUS_MOCKS.find(m => m.id === mockId)
	if (mock) {
		injectMockDeviceStatus(macAddress.value, mock.data, mockId)
	}
}

const resetVariant = () => {
	resetDeviceStatus(macAddress.value)
}

const deviceKey = computed(() => getDeviceKey(macAddress.value))

// Reset state on component mount to ensure fresh auto-check on navigation
onMounted(() => {
	resetDeviceStatus(macAddress.value)
})

const state = computed(() => deviceStatusTests[deviceKey.value])

// Use new shared composable for state management and UI flags
const {
	isLoading: isChecking,
	progress,
	cooldownSeconds,
	result: statusResult,
	severity,
	showResult,
	isDeviceSupported,
	statusText: testStatus
} = useDeviceToolState({
	state,
	deviceNms,
	resultGetter: (state) => {
		if (state && state.result) {
			const result = state.result as DeviceStatusResponse | DeviceStatusResponse[]
			// Handle array response - return first item
			if (Array.isArray(result)) {
				return result.length > 0 ? result[0] : null
			}
			return result
		}
		return null
	},
	severityGetter: (result) => {
		if (!result) return 'default'
		// UISP devices: check nested status and return appropriate severity for color mapping
		if (isUispOffline(result)) return 'disconnected'
		if (isUispOnline(result)) return 'ok'
		return result.severity
	},
	i18nKeys: {
		prefix: 'device-tools.status',
		ready: 'device-tools.status.ready',
		inProgress: 'device-tools.status.checking',
		finishing: 'device-tools.status.finishing'
	}
})

// Use utility functions for health status and text content
const statusDescription = computed(() => getStatusDescription(statusResult.value, t))
const statusLabel = computed(() => getStatusLabel(statusResult.value, t))
const statusMessage = computed(() => getStatusMessage(statusResult.value, t))

// Only Calix devices require serial number (UISP and unknown devices use MAC only)
const requiresSerialNumber = computed(() => deviceNms.value === NMS_TYPES.CALIX)

const handleCheck = async () => {
	if (isChecking.value) {
		return
	}

	await checkDeviceStatus(
		macAddress.value,
		serialNumber.value || undefined,
		deviceNms.value
	)
}

// Auto-check device status when component comes into view
const statusCardRef = ref<HTMLElement | null>(null)
const hasAutoChecked = ref(false)

const shouldAutoCheck = computed(() =>
	isDeviceSupported.value &&
	(deviceNms.value === NMS_TYPES.UISP || !!serialNumber.value) &&
	!isChecking.value &&
	!showResult.value
)

const { stop } = useIntersectionObserver(
	statusCardRef,
	([{ isIntersecting }]) => {
		if (isIntersecting && !hasAutoChecked.value && shouldAutoCheck.value) {
			hasAutoChecked.value = true
			handleCheck()
			stop()
		}
	},
	{
		threshold: 0.5 // Trigger when 50% of component is visible
	}
)
</script>
